var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { createContext, useContext, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { VistaAssetStore } from '@design-stack-vista/vista-assets-sdk';
import { observer } from 'mobx-react-lite';
import { useUploadedAssets } from './hooks/useUploadedAssets';
import { fetchFolderIdForShopper } from './utilities/folderUtilities';
import { showValidAssets } from './utilities/assetFilters';
const UploadManagerContext = createContext(undefined);
/**
 * Provider context for the UploadManagerProvider
 */
export const useUploadManager = () => {
    const result = useContext(UploadManagerContext);
    if (!result) {
        throw new Error('Should be used inside an UploadManagerProvider.');
    }
    return result;
};
/**
 * Provider whose context provides the assetStore, whether or not there are any assets, when the assets expire on, and callbacks for onError and onUpload
 */
export const UploadManagerProvider = observer((props) => {
    const { authProvider, subBrand, experience, writeTenant, assetsExpireOn = 'never', onUpload, onUploadCompleted, onAssetDeleted, onError, children, fetchLimit, folderName, assetStore: assetStoreOverride, } = props;
    const [folderIdForShopper, setFolderIdForShopper] = useState();
    const [visibleAssets, setVisibleAssets] = useState([]);
    const assetStore = assetStoreOverride ||
        useRef(new VistaAssetStore({
            authProvider,
            subBrand,
            experience,
            writeTenant,
        })).current;
    const uploadedAssets = useUploadedAssets({ assetStore, onError, fetchLimit, folderName });
    const hasAssets = assetStore.assets.some((asset) => { var _a, _b, _c; return !((_c = (_b = (_a = asset.data) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.asset) === null || _c === void 0 ? void 0 : _c.hidden); });
    useEffect(() => {
        if (folderName && !folderIdForShopper) {
            // We're accessing another shopper's uploads so we need to be able to upload to that shopper
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const authToken = (yield authProvider.getAuthHeaders()).Authorization.split(' ')[1];
                const folderId = yield fetchFolderIdForShopper({
                    shopperId: folderName,
                    tenant: writeTenant,
                    authToken,
                });
                setFolderIdForShopper(folderId);
            }))();
        }
    }, [folderName, folderIdForShopper, writeTenant, authProvider]);
    const addEventListener = useCallback((type, event) => {
        assetStore.addEventListener(type, event);
    }, [assetStore]);
    const removeEventListener = useCallback((type, event) => {
        assetStore.removeEventListener(type, event);
    }, [assetStore]);
    const updateVisibleAssets = useCallback(() => {
        setVisibleAssets(assetStore.assets.filter(showValidAssets));
    }, [assetStore.assets]);
    const refreshAssets = useCallback(() => {
        if (uploadedAssets.status !== 'ready') {
            return;
        }
        uploadedAssets.refreshAssets();
    }, [uploadedAssets]);
    useEffect(() => {
        if (uploadedAssets.status !== 'ready') {
            return;
        }
        updateVisibleAssets();
    }, [uploadedAssets.status, updateVisibleAssets]);
    const assetDeleted = useCallback((asset) => {
        onAssetDeleted === null || onAssetDeleted === void 0 ? void 0 : onAssetDeleted(asset);
        updateVisibleAssets();
    }, [updateVisibleAssets, onAssetDeleted]);
    const value = useMemo(() => ({
        assetStore,
        hasAssets,
        assetsExpireOn,
        onUpload,
        onUploadCompleted,
        assetDeleted,
        onError,
        fetchingStatus: uploadedAssets.status,
        canFetchMore: uploadedAssets.status === 'ready' || uploadedAssets.status === 'loading-more'
            ? uploadedAssets.canFetchMore
            : undefined,
        fetchMore: uploadedAssets.status === 'ready' ? uploadedAssets.fetchMore : undefined,
        folderIdForShopper,
        folderName,
        addEventListener,
        removeEventListener,
        visibleAssets,
        refreshAssets,
        updateVisibleAssets,
    }), [
        hasAssets,
        assetsExpireOn,
        onUpload,
        onError,
        uploadedAssets,
        onUploadCompleted,
        assetDeleted,
        folderIdForShopper,
        folderName,
        addEventListener,
        removeEventListener,
        assetStore,
        visibleAssets,
        refreshAssets,
        updateVisibleAssets,
    ]);
    return React.createElement(UploadManagerContext.Provider, { value: value }, children);
});
UploadManagerProvider.displayName = 'UploadManagerProvider';
//# sourceMappingURL=UploadManagerProvider.js.map